import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import {
  DataTableExpandedRows,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowEvent,
  DataTableSortEvent,
} from 'primereact/datatable';
import { Message } from 'primereact/message';
import {
  DataGridAtom,
  DataGridColumn,
  PrimeReactButton,
  SearchBarAtom,
  StatusBadgeAtom,
  SwitchAtom,
  useApiClientsOrNull,
  usePlatformAdmin,
} from 'design-system/my-lib';
import AddOrganizationDialogOrganism from './AddOrganizationDialog/AddOrganizationDialogOrganism';
import OrganizationDomainJoinPanel from './OrganizationDomainJoinPanel';
import './OrganizationListPage.scss';

interface OrganizationSummary {
  id: string;
  name: string;
  domain: string | null;
  centerId: string | null;
  centerName: string | null;
  deployedAt: string | null;
  deployedByIdentityId: string | null;
  deployedByEmail: string | null;
  deployedByDisplayName: string | null;
  active: boolean;
  userCount: number;
  identityCount: number;
}

interface OrganizationUserEntry {
  userId: string;
  userName: string;
  identityId: string;
  identityName: string | null;
  identityEmail: string | null;
}

type OrganizationSortField = 'name' | 'deployedAt' | 'userCount';
type SortOrder = 'asc' | 'desc';

const DEFAULT_ROWS = 50;
const ROWS_PER_PAGE = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
const PRIMEREACT_SORT_TO_ORDER: Record<number, SortOrder> = { 1: 'asc', [-1]: 'desc' };
const ORDER_TO_PRIMEREACT_SORT: Record<SortOrder, 1 | -1> = { asc: 1, desc: -1 };

const OrganizationListPage: React.FC = () => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { isPlatformAdmin } = usePlatformAdmin();

  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [sortField, setSortField] = useState<OrganizationSortField>('deployedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [showE2E, setShowE2E] = useState(false);

  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [listVersion, setListVersion] = useState(0);

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [usersByOrg, setUsersByOrg] = useState<
    Record<string, { loading: boolean; data: OrganizationUserEntry[] | null; error: string | null }>
  >({});

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setFirst(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!apiClients?.graphClient) return;
    // Authz lives in <RequireMetasphereAdmin> + server-side isPlatformAdmin —
    // the graphClient call is safe here because the route is already gated.
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await apiClients.graphClient.get('/admin/organizations', {
          params: {
            search: search || undefined,
            limit: rows,
            offset: first,
            sortField,
            sortOrder,
            showDeactivated: showDeactivated || undefined,
            showE2E: showE2E || undefined,
          },
        });
        if (cancelled) return;
        setOrganizations(response.organizations);
        setTotalRecords(response.total);
      } catch {
        if (cancelled) return;
        setError(t('admin.organizations.errors.fetchFailed'));
        setOrganizations([]);
        setTotalRecords(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, search, first, rows, sortField, sortOrder, showDeactivated, showE2E, t, listVersion]);

  const handlePageChange = useCallback((event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    const field = event.sortField as OrganizationSortField;
    if (field !== 'name' && field !== 'deployedAt' && field !== 'userCount') return;
    const nextOrder = PRIMEREACT_SORT_TO_ORDER[event.sortOrder ?? -1] ?? 'desc';
    let sortChanged = false;
    setSortField((current) => {
      if (current !== field) sortChanged = true;
      return field;
    });
    setSortOrder((current) => {
      if (current !== nextOrder) sortChanged = true;
      return nextOrder;
    });
    // PrimeReact's DataTable fires onSort alongside onPage; only reset offset
    // when the sort actually changed so pagination clicks aren't trampled.
    if (sortChanged) setFirst(0);
  }, []);

  const handleShowDeactivatedChange = useCallback((value: boolean) => {
    setShowDeactivated(value);
    setFirst(0);
  }, []);

  const handleShowE2EChange = useCallback((value: boolean) => {
    setShowE2E(value);
    setFirst(0);
  }, []);

  const fetchUsersForOrg = useCallback(
    async (orgId: string) => {
      if (!apiClients?.graphClient) return;
      setUsersByOrg((prev) => ({
        ...prev,
        [orgId]: { loading: true, data: prev[orgId]?.data ?? null, error: null },
      }));
      try {
        // Authz: <RequireMetasphereAdmin> + server-side isPlatformAdmin gate.
        const response = await apiClients.graphClient.get(
          `/admin/organizations/${encodeURIComponent(orgId)}/users`,
        );
        setUsersByOrg((prev) => ({
          ...prev,
          [orgId]: {
            loading: false,
            data: response.users || [],
            error: null,
          },
        }));
      } catch {
        setUsersByOrg((prev) => ({
          ...prev,
          [orgId]: {
            loading: false,
            data: null,
            error: t('admin.organizations.users.fetchFailed'),
          },
        }));
      }
    },
    [apiClients, t],
  );

  const ensureUsersLoaded = useCallback(
    (orgId: string) => {
      const cached = usersByOrg[orgId];
      if (!cached || (!cached.loading && !cached.data && !cached.error)) {
        fetchUsersForOrg(orgId);
      }
    },
    [usersByOrg, fetchUsersForOrg],
  );

  const handleRowToggle = useCallback(
    (event: { data: DataTableExpandedRows }) => {
      setExpandedRows(event.data);
    },
    [],
  );

  const handleRowExpand = useCallback(
    (event: DataTableRowEvent) => {
      const row = event.data as OrganizationSummary;
      if (row?.id) ensureUsersLoaded(row.id);
    },
    [ensureUsersLoaded],
  );

  const handleRowClick = useCallback(
    (event: DataTableRowClickEvent) => {
      const target = event.originalEvent.target as HTMLElement;
      if (target?.closest('button, a, input, [role="button"]')) return;
      const row = event.data as OrganizationSummary;
      if (!row?.id) return;
      setExpandedRows((prev) => {
        const next = { ...(prev as Record<string, boolean>) };
        if (next[row.id]) {
          delete next[row.id];
        } else {
          next[row.id] = true;
        }
        return next as DataTableExpandedRows;
      });
      ensureUsersLoaded(row.id);
    },
    [ensureUsersLoaded],
  );

  const emptyMessage = useMemo(
    () =>
      search
        ? t('admin.organizations.emptyNoResults')
        : t('admin.organizations.empty'),
    [search, t],
  );

  const formatDate = useCallback((value: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? value : date.toLocaleDateString();
  }, []);

  const nameTemplate = useCallback(
    (rowData: OrganizationSummary) => <span>{rowData.name || '—'}</span>,
    [],
  );

  const domainTemplate = useCallback(
    (rowData: OrganizationSummary) => <span>{rowData.domain || '—'}</span>,
    [],
  );

  const centerNameTemplate = useCallback(
    (rowData: OrganizationSummary) => <span>{rowData.centerName || '—'}</span>,
    [],
  );

  const deployedAtTemplate = useCallback(
    (rowData: OrganizationSummary) => <span>{formatDate(rowData.deployedAt)}</span>,
    [formatDate],
  );

  const deployedByTemplate = useCallback(
    (rowData: OrganizationSummary) => (
      <span title={rowData.deployedByDisplayName ?? undefined}>
        {rowData.deployedByEmail || '—'}
      </span>
    ),
    [],
  );

  const statusTemplate = useCallback(
    (rowData: OrganizationSummary) => (
      <StatusBadgeAtom
        status={rowData.active ? 'active' : 'deactivated'}
        label={
          rowData.active
            ? t('admin.organizations.status.active')
            : t('admin.organizations.status.deactivated')
        }
      />
    ),
    [t],
  );

  const userCountTemplate = useCallback(
    (rowData: OrganizationSummary) => (
      <span className="organization-list-page__metric">{rowData.userCount}</span>
    ),
    [],
  );

  const identityCountTemplate = useCallback(
    (rowData: OrganizationSummary) => (
      <span className="organization-list-page__metric">{rowData.identityCount}</span>
    ),
    [],
  );

  const columns: DataGridColumn[] = useMemo(
    () => [
      {
        field: 'name',
        header: t('admin.organizations.columns.name'),
        body: nameTemplate,
        sortable: true,
      },
      {
        field: 'domain',
        header: t('admin.organizations.columns.domain'),
        body: domainTemplate,
      },
      {
        field: 'centerName',
        header: t('admin.organizations.columns.center'),
        body: centerNameTemplate,
      },
      {
        field: 'deployedAt',
        header: t('admin.organizations.columns.deployedAt'),
        body: deployedAtTemplate,
        sortable: true,
        width: '140px',
      },
      {
        field: 'deployedByEmail',
        header: t('admin.organizations.columns.deployedBy'),
        body: deployedByTemplate,
      },
      {
        field: 'active',
        header: t('admin.organizations.columns.status'),
        body: statusTemplate,
        width: '110px',
      },
      {
        field: 'userCount',
        header: t('admin.organizations.columns.users'),
        body: userCountTemplate,
        sortable: true,
        width: '90px',
      },
      {
        field: 'identityCount',
        header: t('admin.organizations.columns.identities'),
        body: identityCountTemplate,
        width: '90px',
      },
    ],
    [
      t,
      nameTemplate,
      domainTemplate,
      centerNameTemplate,
      deployedAtTemplate,
      deployedByTemplate,
      statusTemplate,
      userCountTemplate,
      identityCountTemplate,
    ],
  );

  const rowExpansionTemplate = useCallback(
    (rowData: OrganizationSummary) => {
      const cached = usersByOrg[rowData.id];

      const detailsBlock = (
        <dl className="organization-tray__details">
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.name')}</dt>
            <dd>{rowData.name || '—'}</dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.domain')}</dt>
            <dd>{rowData.domain || '—'}</dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.center')}</dt>
            <dd>{rowData.centerName || '—'}</dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.deployedAt')}</dt>
            <dd>{formatDate(rowData.deployedAt)}</dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.deployedBy')}</dt>
            <dd>
              {rowData.deployedByEmail || '—'}
              {rowData.deployedByDisplayName ? (
                <span className="organization-tray__deployed-by-name">
                  {' '}
                  ({rowData.deployedByDisplayName})
                </span>
              ) : null}
            </dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.status')}</dt>
            <dd>
              <StatusBadgeAtom
                status={rowData.active ? 'active' : 'deactivated'}
                label={
                  rowData.active
                    ? t('admin.organizations.status.active')
                    : t('admin.organizations.status.deactivated')
                }
              />
            </dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.userCount')}</dt>
            <dd>{rowData.userCount}</dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.identityCount')}</dt>
            <dd>{rowData.identityCount}</dd>
          </div>
          <div className="organization-tray__details-row">
            <dt>{t('admin.organizations.tray.id')}</dt>
            <dd>
              <code className="organization-tray__id">{rowData.id}</code>
            </dd>
          </div>
        </dl>
      );

      let usersContent: React.ReactNode;
      if (!cached || cached.loading) {
        usersContent = (
          <div className="organization-tray__users-state">
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            <span>{t('admin.organizations.users.loading')}</span>
          </div>
        );
      } else if (cached.error) {
        usersContent = (
          <div className="organization-tray__users-state organization-tray__users-state--error">
            {cached.error}
          </div>
        );
      } else if ((cached.data ?? []).length === 0) {
        usersContent = (
          <div className="organization-tray__users-state">
            {t('admin.organizations.users.empty')}
          </div>
        );
      } else {
        usersContent = (
          <table className="organization-tray__users-table">
            <thead>
              <tr>
                <th>{t('admin.organizations.tray.users.name')}</th>
                <th>{t('admin.organizations.tray.users.identity')}</th>
                <th>{t('admin.organizations.tray.users.email')}</th>
              </tr>
            </thead>
            <tbody>
              {(cached.data ?? []).map((entry) => (
                <tr key={`${entry.userId}:${entry.identityId}`}>
                  <td>{entry.userName || '—'}</td>
                  <td>{entry.identityName || '—'}</td>
                  <td>{entry.identityEmail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      return (
        <div className="organization-tray">
          <section className="organization-tray__section">
            <h3 className="organization-tray__section-title">
              {t('admin.organizations.tray.detailsTitle')}
            </h3>
            {detailsBlock}
            <OrganizationDomainJoinPanel
              organizationSphereId={rowData.id}
              organizationName={rowData.name}
              domain={rowData.domain}
            />
          </section>
          <section className="organization-tray__section">
            <h3 className="organization-tray__section-title">
              {t('admin.organizations.tray.usersTitle')}
            </h3>
            {usersContent}
          </section>
        </div>
      );
    },
    [usersByOrg, formatDate, t],
  );

  return (
    <div className="organization-list-page" data-testid="organization-list-page">
      <header className="organization-list-page__page-header">
        <h1 className="organization-list-page__title">{t('admin.organizations.title')}</h1>
        {isPlatformAdmin && (
          <PrimeReactButton
            label={t('admin.organizations.addButton')}
            onClick={() => setAddDialogVisible(true)}
            data-testid="add-organization-button"
          />
        )}
      </header>

      <AddOrganizationDialogOrganism
        visible={addDialogVisible}
        onHide={() => setAddDialogVisible(false)}
        onSuccess={() => {
          setAddDialogVisible(false);
          setListVersion((v) => v + 1);
        }}
      />

      <div className="organization-list-page__controls">
        <SwitchAtom
          inputId="organization-list-show-deactivated"
          label={t('admin.organizations.toggleShowDeactivated')}
          checked={showDeactivated}
          onChange={handleShowDeactivatedChange}
        />
        <SwitchAtom
          inputId="organization-list-show-e2e"
          label={t('admin.organizations.toggleShowE2E')}
          checked={showE2E}
          onChange={handleShowE2EChange}
        />
        <div className="organization-list-page__search">
          <SearchBarAtom
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('admin.organizations.searchPlaceholder')}
          />
        </div>
      </div>

      {error ? (
        <Message
          severity="error"
          text={error}
          className="organization-list-page__error"
        />
      ) : (
        <DataGridAtom
          data={organizations}
          columns={columns}
          loading={loading}
          paginator
          rows={rows}
          rowsPerPageOptions={ROWS_PER_PAGE}
          first={first}
          onPage={handlePageChange}
          totalRecords={totalRecords}
          lazy
          onRowClick={handleRowClick}
          rowClassName={() => 'organization-list-page__row'}
          emptyMessage={emptyMessage}
          dataKey="id"
          showHeader={false}
          expandedRows={expandedRows}
          onRowToggle={handleRowToggle}
          onRowExpand={handleRowExpand}
          rowExpansionTemplate={rowExpansionTemplate}
          sortField={sortField}
          sortOrder={ORDER_TO_PRIMEREACT_SORT[sortOrder]}
          onSort={handleSort}
          className="organization-list-page__table"
          data-testid="organization-list-table"
        />
      )}
    </div>
  );
};

export default OrganizationListPage;
